import { makeAutoObservable } from "mobx";
import { MapillarySequence } from "../../map/models/Mapillary";
import { RootStore } from "../../shared/stores/RootStore";

export class MapillaryStore {
  rootStore: RootStore;
  sequences: MapillarySequence[] = [];
  selectedSequenceId?: string = undefined;
  selectedSinglePointId?: string = undefined;
  selectedPointIds = new Set<string>();
  removedPointIds = new Set<string>();

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  setSequences(sequences: MapillarySequence[]) {
    this.sequences = sequences;
    this.rootStore.mapStore.mapillaryMapSource?.update(sequences);
  }

  selectSequence(sequenceId: string) {
    const changed = this.selectedSequenceId !== sequenceId;

    this.selectedSequenceId = sequenceId;

    if (changed) {
      this.selectedSinglePointId = undefined;
      this.selectedPointIds.clear();
      this.removedPointIds.clear();
    }

    this.rootStore.mapStore.onSelectedMapillarySequenceChange(sequenceId);
    this.rootStore.mapStore.mapillaryMapSource?.update(this.sequences);
  }

  togglePointSelected(imageId: string) {
    if (this.selectedPointIds.has(imageId)) {
      this.selectedPointIds.delete(imageId);
    } else {
      this.selectedPointIds.add(imageId);
    }

    this.rootStore.mapStore.mapillaryMapSource?.update(this.sequences);
  }

  togglePointRemoved(imageId: string) {
    if (this.removedPointIds.has(imageId)) {
      this.removedPointIds.delete(imageId);
    } else {
      this.removedPointIds.add(imageId);
    }

    this.rootStore.mapStore.mapillaryMapSource?.update(this.sequences);
  }

  removeSelectedPoints() {
    this.selectedPointIds.forEach((imageId) => {
      this.removedPointIds.add(imageId);
    });

    this.selectedPointIds.clear();
    this.selectedSinglePointId = undefined;
    this.rootStore.mapStore.mapillaryMapSource?.update(this.sequences);
  }

  reset() {
    this.sequences = [];
    this.selectedSequenceId = undefined;
    this.selectedSinglePointId = undefined;
    this.selectedPointIds.clear();
    this.removedPointIds.clear();

    this.rootStore.mapStore.onSelectedMapillarySequenceChange(undefined);
    this.rootStore.mapStore.mapillaryMapSource?.update([]);
  }

  clearSelectedPoints() {
    this.selectedSinglePointId = undefined;
    this.selectedPointIds.clear();
    this.rootStore.mapStore.mapillaryMapSource?.update(this.sequences);
  }

  get selectedSequenceKeptPoints() {
    const seq = this.sequences.find(
      (s) => s.sequenceId === this.selectedSequenceId,
    );
    if (!seq) return [];

    return seq.points.filter((p) => !this.removedPointIds.has(p.imageId));
  }

  get excludedImageIds(): string[] {
    return Array.from(this.removedPointIds);
  }
}
